import React, { useState } from 'react';
import { importAPI } from '../utils/api';
import { Upload, FileSpreadsheet, Check, AlertCircle, Loader2 } from 'lucide-react';

const DataImport = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [dragging, setDragging] = useState(false);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.xlsx') && !droppedFile.name.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(droppedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Step 1: Analyze
      const analyzeResponse = await importAPI.analyze(formData);
      const { import_id } = analyzeResponse.data;

      // Step 2: Execute import
      await importAPI.execute(import_id, true);

      setSuccess(true);
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Data Import</h1>
        <p className="text-slate-500 mt-1">AI-powered import wizard for your Excel data</p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-8 max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <FileSpreadsheet className="mx-auto h-16 w-16 text-blue-500 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Upload Your Excel File
          </h3>
          <p className="text-slate-600">
            Our AI will analyze and import your data automatically
          </p>
        </div>

        {/* File Upload */}
        <div 
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragging 
              ? 'border-primary bg-primary/5' 
              : 'border-slate-300 hover:border-primary'
          }`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-upload"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-upload-input"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="h-12 w-12 text-slate-400 mb-3" />
            <span className="text-sm font-medium text-slate-900 mb-1">
              Click to upload or drag and drop
            </span>
            <span className="text-xs text-slate-500">Excel files only (.xlsx, .xls)</span>
          </label>
        </div>

        {/* Selected File */}
        {file && !success && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg mb-6">
            <FileSpreadsheet className="text-green-600" size={24} />
            <div className="flex-1">
              <p className="font-medium text-green-900">{file.name}</p>
              <p className="text-sm text-green-700">
                {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
            <Check className="text-green-600" size={20} />
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-2 p-4 bg-red-50 rounded-lg mb-6">
            <AlertCircle className="text-red-600 mt-0.5" size={20} />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="flex items-start gap-2 p-4 bg-green-50 rounded-lg mb-6">
            <Check className="text-green-600 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-green-900">Import Successful!</p>
              <p className="text-sm text-green-700 mt-1">
                Your data has been imported. View your deals to see the results.
              </p>
            </div>
          </div>
        )}

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={!file || loading}
          className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="upload-button"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Importing...
            </>
          ) : (
            <>
              <Upload size={20} />
              Import Data
            </>
          )}
        </button>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">What happens next:</h4>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>✓ AI analyzes your Excel structure</li>
            <li>✓ Field mappings are suggested</li>
            <li>✓ Duplicates are detected</li>
            <li>✓ Data is cleaned and imported</li>
            <li>✓ All changes are logged</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataImport;
